import React, { useState, useCallback } from 'react';
import * as Bixby from '$/core';
import BixCheckbox from '$/components/global/bixCheckbox';
import { CompanyService } from '$/services';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import './DedicatedFinancialsModal.scss';

export interface IDedicatedFinancialsModalProps {
  currentCompany: Bixby.Models.AdminCompany;
  setCurrentCompany: (companyId: string) => Promise<void>;
  dispatchToastError: (message: string) => void;
  showCreateDedicatedFinancialsModal: boolean;
  setShowCreateDedicatedFinancialsModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DedicatedFinancialsModal: React.FC<IDedicatedFinancialsModalProps> = ({
  currentCompany,
  setCurrentCompany,
  dispatchToastError,
  showCreateDedicatedFinancialsModal,
  setShowCreateDedicatedFinancialsModal,
}) => {
  const [isCreatingDedicatedFinancials, setIsCreatingDedicatedFinancials] = useState(false);
  const [dedicatedFinancialsConfirmed, setDedicatedFinancialsConfirmed] = useState(false);

  const handleClose = useCallback(() => {
    setIsCreatingDedicatedFinancials(false);
    setShowCreateDedicatedFinancialsModal(false);
  }, [setIsCreatingDedicatedFinancials, setShowCreateDedicatedFinancialsModal]);

  const handleCreateDedicatedFinancials = useCallback(async () => {
    try {
      setIsCreatingDedicatedFinancials(true);
      await CompanyService.createDedicatedFinancialsAsync(currentCompany.id);
      await setCurrentCompany(currentCompany.id);
    } catch (error) {
      dispatchToastError('Unable to create dedicated financials. Please try again later.');
    } finally {
      handleClose();
    }
  }, [currentCompany.id, setCurrentCompany, dispatchToastError, handleClose]);

  return showCreateDedicatedFinancialsModal ? (
    <Modal active={true} hideClose>
      <div className="create_dedicated_financials_modal">
        <div className="content">
          <p>
            Please note setting dedicated financials immediately expects a financial model to be present and published
            for this Private Company.
          </p>
          <p>Please use the link below to add the financial model and publish it prior to confirming below.</p>
          <p>Additionally, please note that this operation is irreversible.</p>
          <a className="link" href={currentCompany.documentLibraryWebUrl} target="_blank" rel="noopener noreferrer">
            Financial Access URL (SharePoint)
          </a>
        </div>
        <BixCheckbox
          id="dedicatedFinancialsConfirmation"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDedicatedFinancialsConfirmed(e.currentTarget.checked)
          }>
          I confer this Private Company will have dedicated financials.
        </BixCheckbox>
        <div className="modal_button_container">
          <BixButton
            type="button"
            onClick={handleCreateDedicatedFinancials}
            useLoading
            isLoading={isCreatingDedicatedFinancials}
            disabled={isCreatingDedicatedFinancials || !dedicatedFinancialsConfirmed}>
            Confirm
          </BixButton>
          <BixButton light type="button" onClick={handleClose} disabled={isCreatingDedicatedFinancials}>
            Cancel
          </BixButton>
        </div>
      </div>
    </Modal>
  ) : (
    <></>
  );
};
